import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  Card,
  CardHeader,
  Avatar,
  Divider,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { MuiElegantErrorModal } from '../components/ui/mui-elegant-error-modal';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { leaveRequestService } from '../services/leaveRequestService';
import { employeeService } from '../services/employeeService';
import { LeaveRequest, Employee } from '../types';
import { formatDate, calculateWorkingDays } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';

const LeaveRequestsPage: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentRequest, setCurrentRequest] = useState<Partial<LeaveRequest>>({});
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());

  const fetchLeaveRequests = useCallback(async () => {
    try {
      let requests;
      if (statusFilter === 'all') {
        requests = await leaveRequestService.getAll();
      } else {
        requests = await leaveRequestService.getByStatus(statusFilter as 'pending' | 'approved' | 'rejected');
      }
      setLeaveRequests(requests);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    }
  }, [statusFilter]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const employees = await employeeService.getAll();
        setEmployees(employees);
        await fetchLeaveRequests();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchLeaveRequests]);



  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setCurrentRequest({
      status: 'pending',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      leave_type: 'vacation',
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (request: LeaveRequest) => {
    setDialogMode('edit');
    setCurrentRequest({ ...request });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRequest({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentRequest(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setCurrentRequest(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveRequest = async () => {
    try {
      if (dialogMode === 'add') {
        await leaveRequestService.create(currentRequest as Omit<LeaveRequest, 'id' | 'created_at' | 'updated_at'>);
      } else {
        if (currentRequest.id) {
          await leaveRequestService.update(currentRequest.id, currentRequest);
        }
      }

      // Refresh leave requests list
      await fetchLeaveRequests();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving leave request:', error);
      // Show elegant modal for insufficient balance errors
      if (error.message && error.message.includes('Insufficient')) {
        setErrorModal({
          isOpen: true,
          title: 'Leave Request Failed',
          message: error.message,
          type: 'error'
        });
      } else {
        setErrorModal({
          isOpen: true,
          title: 'Error: Failed to Save Leave Request',
          message: error.message || 'Unknown error occurred',
          type: 'error'
        });
      }
    }
  };

  const handleDeleteRequest = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this leave request?')) {
      try {
        await leaveRequestService.delete(id);
        // Refresh leave requests list
        await fetchLeaveRequests();
      } catch (error) {
        console.error('Error deleting leave request:', error);
      }
    }
  };

  const handleApproveRequest = async (id: string) => {
    try {
      if (user) {
        await leaveRequestService.approve(id, user.id);
        // Refresh leave requests list
        await fetchLeaveRequests();
      }
    } catch (error) {
      console.error('Error approving leave request:', error);
    }
  };

  const handleRejectRequest = async (id: string) => {
    try {
      if (user) {
        await leaveRequestService.reject(id, user.id);
        // Refresh leave requests list
        await fetchLeaveRequests();
      }
    } catch (error) {
      console.error('Error rejecting leave request:', error);
    }
  };

  const handleStatusFilterChange = async (e: React.ChangeEvent<{ value: unknown }>) => {
    const status = e.target.value as string;
    setStatusFilter(status);
    setLoading(true);
    try {
      let requests;
      if (status === 'all') {
        requests = await leaveRequestService.getAll();
      } else {
        requests = await leaveRequestService.getByStatus(status as 'pending' | 'approved' | 'rejected');
      }
      setLeaveRequests(requests);
    } catch (error) {
      console.error('Error filtering leave requests:', error);
    } finally {
      setLoading(false);
    }
  };



  // Calculate leave duration (working days only)
  const getLeaveDuration = (startDate: string, endDate: string): number => {
    return calculateWorkingDays(startDate, endDate); // Calculate working days excluding weekends
  };

  // Handle accordion expansion
  const handleAccordionChange = (employeeId: string) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedEmployees(newExpanded);
  };

  // Group leave requests by employee
  const groupLeaveRequestsByEmployee = () => {
    const grouped: { [key: string]: { employee: Employee; requests: LeaveRequest[] } } = {};
    
    leaveRequests.forEach(request => {
      const employee = employees.find(emp => emp.id === request.employee_id);
      if (employee) {
        if (!grouped[request.employee_id]) {
          grouped[request.employee_id] = {
            employee,
            requests: []
          };
        }
        grouped[request.employee_id].requests.push(request);
      }
    });
    
    // Sort requests within each group by start date (newest first)
    Object.values(grouped).forEach(group => {
      group.requests.sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());
    });
    
    return grouped;
  };



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="div">
          Leave Requests
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          New Request
        </Button>
      </Box>

      {/* Status Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange as any}
                label="Status Filter"
              >
                <MenuItem value="all">All Requests</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Leave Requests Grouped by Employee */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Leave Requests by Employee
        </Typography>
        {Object.entries(groupLeaveRequestsByEmployee()).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No leave requests found.
          </Typography>
        ) : (
          Object.entries(groupLeaveRequestsByEmployee()).map(([employeeId, { employee, requests }]) => (
            <Accordion
              key={employeeId}
              expanded={expandedEmployees.has(employeeId)}
              onChange={() => handleAccordionChange(employeeId)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    <PersonIcon />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {employee.first_name} {employee.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {employee.department} • {employee.position}
                    </Typography>
                  </Box>
                  <Badge badgeContent={requests.length} color="primary" sx={{ mr: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Requests
                    </Typography>
                  </Badge>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />
                {requests.map((request, index) => (
                  <Card key={request.id} variant="outlined" sx={{ mb: index < requests.length - 1 ? 2 : 0 }}>
                    <CardHeader
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={request.leave_type.charAt(0).toUpperCase() + request.leave_type.slice(1)}
                            color={
                              request.leave_type === 'vacation'
                                ? 'primary'
                                : request.leave_type === 'sick'
                                ? 'error'
                                : request.leave_type === 'personal'
                                ? 'warning'
                                : 'default'
                            }
                            size="small"
                          />
                          <Chip
                            label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            color={
                              request.status === 'approved'
                                ? 'success'
                                : request.status === 'rejected'
                                ? 'error'
                                : 'warning'
                            }
                            size="small"
                          />
                        </Box>
                      }
                      subheader={
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(request.start_date)} - {formatDate(request.end_date)} 
                          ({getLeaveDuration(request.start_date, request.end_date)} days)
                        </Typography>
                      }
                      action={
                        <Box sx={{ display: 'flex' }}>
                          {isAdmin() && request.status === 'pending' && (
                            <>
                              <IconButton
                                color="success"
                                onClick={() => handleApproveRequest(request.id)}
                                title="Approve"
                              >
                                <ApproveIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleRejectRequest(request.id)}
                                title="Reject"
                              >
                                <RejectIcon />
                              </IconButton>
                            </>
                          )}
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenEditDialog(request)}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteRequest(request.id)}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    />
                    {request.reason && (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Reason:</strong> {request.reason}
                        </Typography>
                      </Box>
                    )}
                  </Card>
                ))}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Paper>

      {/* Add/Edit Leave Request Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'New Leave Request' : 'Edit Leave Request'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <FormControl sx={{ width: 'calc(50% - 8px)' }}>
              <InputLabel>Employee</InputLabel>
              <Select
                name="employee_id"
                value={currentRequest.employee_id || ''}
                onChange={handleSelectChange}
                label="Employee"
                required
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {`${employee.first_name} ${employee.last_name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: 'calc(50% - 8px)' }}>
              <InputLabel>Leave Type</InputLabel>
              <Select
                name="leave_type"
                value={currentRequest.leave_type || 'sick'}
                onChange={handleSelectChange}
                label="Leave Type"
                required
              >
                <MenuItem value="sick">Sick Leave</MenuItem>
                <MenuItem value="vacation">Vacation</MenuItem>
                <MenuItem value="personal">Personal Leave</MenuItem>
                <MenuItem value="maternity">Maternity Leave</MenuItem>
                <MenuItem value="paternity">Paternity Leave</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              name="start_date"
              label="Start Date"
              type="date"
              value={currentRequest.start_date || ''}
              onChange={handleInputChange}
              required
              InputLabelProps={{ shrink: true }}
              sx={{ width: 'calc(50% - 8px)' }}
            />
            <TextField
              name="end_date"
              label="End Date"
              type="date"
              value={currentRequest.end_date || ''}
              onChange={handleInputChange}
              required
              InputLabelProps={{ shrink: true }}
              sx={{ width: 'calc(50% - 8px)' }}
            />
            <TextField
              name="reason"
              label="Reason"
              value={currentRequest.reason || ''}
              onChange={handleInputChange}
              multiline
              rows={3}
              sx={{ width: '100%' }}
            />
            {isAdmin() && (
              <FormControl sx={{ width: 'calc(50% - 8px)' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={currentRequest.status || 'pending'}
                  onChange={handleSelectChange}
                  label="Status"
                  required
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveRequest} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Elegant Error Modal */}
      <MuiElegantErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
        type={errorModal.type}
      />
    </Box>
  );
};

export default LeaveRequestsPage;